(function () {
  const DEFAULT_CONSTANTS = {
    MIN_QUANTITY: 20,
    COLLAR_QUANTITY_INCREMENT: 10,
    FLEX_MIN_QUANTITY: 40,
    GATEWAY_HALF_THRESHOLD: 20,
    OWNERSHIP_COLLAR_PRICE: 79,
    FLEX_COLLAR_PRICE: 39.5,
    DAIRYMIND_MONTHLY_PER_COW: 3.99,
    FLEX_COMMITMENT_MONTHS: 12,
    GATEWAY_STANDARD_PRICE: 699,
    GATEWAY_HALF_PRICE: 349.5,
    COMPETITOR_COLLAR_BENCHMARK: 120
  };

  function dollarsFromCents(value, fallback) {
    const cents = Number(value);
    if (Number.isFinite(cents) && cents > 0) return cents / 100;
    return fallback;
  }

  function positiveInteger(value) {
    const parsed = Number.parseInt(value, 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }

  function normalizeCollarQuantity(value, providedConstants) {
    const constants = providedConstants || DEFAULT_CONSTANTS;
    const minimum = positiveInteger(constants.MIN_QUANTITY) || 20;
    const increment = positiveInteger(constants.COLLAR_QUANTITY_INCREMENT) || 10;
    const quantity = Number.parseInt(value, 10);
    if (!Number.isInteger(quantity) || quantity <= 0) return minimum;

    return Math.max(
      minimum,
      Math.ceil(quantity / increment) * increment
    );
  }

  function isValidCollarQuantity(value, providedConstants) {
    const constants = providedConstants || DEFAULT_CONSTANTS;
    const minimum = positiveInteger(constants.MIN_QUANTITY) || 20;
    const increment = positiveInteger(constants.COLLAR_QUANTITY_INCREMENT) || 10;
    const quantity = positiveInteger(value);
    return Boolean(quantity && quantity >= minimum && quantity % increment === 0);
  }

  function getOwnershipGatewayPrice(quantity, constants) {
    const qty = positiveInteger(quantity);
    if (!qty || qty < constants.MIN_QUANTITY) return null;
    if (qty >= constants.FLEX_MIN_QUANTITY) return 0;
    if (qty >= constants.GATEWAY_HALF_THRESHOLD) return constants.GATEWAY_HALF_PRICE;
    return constants.GATEWAY_STANDARD_PRICE;
  }

  function getOwnershipGatewaySavings(quantity, constants) {
    const price = getOwnershipGatewayPrice(quantity, constants);
    if (price === null) return 0;
    return constants.GATEWAY_STANDARD_PRICE - price;
  }

  function isFlexEligible(quantity, constants) {
    const qty = positiveInteger(quantity);
    return Boolean(qty && qty >= constants.FLEX_MIN_QUANTITY);
  }

  function getFlexGatewayPrice(quantity, constants) {
    return isFlexEligible(quantity, constants) ? constants.GATEWAY_HALF_PRICE : null;
  }

  function create(options) {
    const settings = options || {};
    const constants = Object.assign({}, DEFAULT_CONSTANTS, settings.constants || {});
    const commerce = settings.commerce || {};
    const ownershipCollarPrice = dollarsFromCents(commerce.ownershipProduct && commerce.ownershipProduct.price, constants.OWNERSHIP_COLLAR_PRICE);
    const flexCollarPrice = dollarsFromCents(commerce.flexHardwareProduct && commerce.flexHardwareProduct.price, constants.FLEX_COLLAR_PRICE);
    const softwareMonthlyPrice = dollarsFromCents(commerce.flexSoftwareProduct && commerce.flexSoftwareProduct.price, constants.DAIRYMIND_MONTHLY_PER_COW);
    const gatewayStandardPrice = dollarsFromCents(commerce.gatewayProduct && commerce.gatewayProduct.price, constants.GATEWAY_STANDARD_PRICE);
    constants.GATEWAY_STANDARD_PRICE = gatewayStandardPrice;
    constants.GATEWAY_HALF_PRICE = gatewayStandardPrice / 2;

    function calculate(rawInput, billingTiming) {
      const input = rawInput && typeof rawInput === 'object' ? rawInput : { herdSize: rawInput };
      const herdSize = positiveInteger(input.herdSize) || 0;
      const recommendedMinimum = normalizeCollarQuantity(herdSize || constants.MIN_QUANTITY, constants);
      const requestedOrderQuantity = positiveInteger(input.orderQuantity);
      const orderQuantity = isValidCollarQuantity(requestedOrderQuantity, constants)
        ? requestedOrderQuantity
        : herdSize
          ? recommendedMinimum
          : 0;
      const meetsMinimum = orderQuantity >= constants.MIN_QUANTITY;
      const flexEligible = isFlexEligible(orderQuantity, constants);
      const ownershipGatewayPrice = getOwnershipGatewayPrice(orderQuantity, constants);
      const ownershipGatewaySavings = getOwnershipGatewaySavings(orderQuantity, constants);
      const flexGatewayPrice = getFlexGatewayPrice(orderQuantity, constants);
      const ownershipCollarSubtotal = orderQuantity * ownershipCollarPrice;
      const ownershipTotal = meetsMinimum ? ownershipCollarSubtotal + ownershipGatewayPrice : 0;
      const flexCollarSubtotal = orderQuantity * flexCollarPrice;
      const flexMonthlySoftwareFee = orderQuantity * softwareMonthlyPrice;
      const flexTwelveMonthSoftwareFee = flexMonthlySoftwareFee * constants.FLEX_COMMITMENT_MONTHS;
      const flexTwelveMonthTotal = flexEligible ? flexCollarSubtotal + flexGatewayPrice + flexTwelveMonthSoftwareFee : null;
      const flexEstimatedDueToday = flexEligible
        ? flexCollarSubtotal + flexGatewayPrice + (billingTiming === 'first_month_at_checkout' ? flexMonthlySoftwareFee : 0)
        : null;
      const ownershipCollarSavings = meetsMinimum ? orderQuantity * (constants.COMPETITOR_COLLAR_BENCHMARK - ownershipCollarPrice) : 0;
      const ownershipBenchmarkSavings = ownershipCollarSavings + ownershipGatewaySavings;
      const flexCollarUpfrontSavings = flexEligible ? orderQuantity * (constants.COMPETITOR_COLLAR_BENCHMARK - flexCollarPrice) : 0;
      const flexUpfrontHardwareSavings = flexEligible ? flexCollarUpfrontSavings + (constants.GATEWAY_STANDARD_PRICE - flexGatewayPrice) : null;

      return {
        herdSize: herdSize,
        orderQuantity: orderQuantity,
        meetsMinimum: meetsMinimum,
        flexEligible: flexEligible,
        minimumQuantity: constants.MIN_QUANTITY,
        recommendedMinimum: recommendedMinimum,
        quantityIncrement: constants.COLLAR_QUANTITY_INCREMENT,
        ownership: {
          collarPrice: ownershipCollarPrice,
          collarSubtotal: ownershipCollarSubtotal,
          gatewayPrice: ownershipGatewayPrice,
          gatewaySavings: ownershipGatewaySavings,
          total: ownershipTotal,
          softwareFee: 0,
          benchmarkCollarSavings: ownershipCollarSavings,
          benchmarkSavings: ownershipBenchmarkSavings
        },
        flex: {
          collarPrice: flexCollarPrice,
          collarSubtotal: flexCollarSubtotal,
          softwareMonthlyPerCow: softwareMonthlyPrice,
          gatewayPrice: flexGatewayPrice,
          gatewaySavings: flexEligible ? constants.GATEWAY_STANDARD_PRICE - flexGatewayPrice : 0,
          monthlySoftwareFee: flexMonthlySoftwareFee,
          twelveMonthSoftwareFee: flexTwelveMonthSoftwareFee,
          twelveMonthTotal: flexTwelveMonthTotal,
          estimatedDueToday: flexEstimatedDueToday,
          benchmarkCollarSavings: flexCollarUpfrontSavings,
          upfrontHardwareSavings: flexUpfrontHardwareSavings
        },
        comparisons: {
          ownershipVsFlex: flexEligible ? Math.max(0, flexTwelveMonthTotal - ownershipTotal) : null,
          flexVsOwnership: flexEligible ? Math.max(0, ownershipTotal - flexTwelveMonthTotal) : null,
          flexUpfrontReduction: flexEligible ? Math.max(0, ownershipTotal - flexEstimatedDueToday) : null
        },
        thresholds: {
          nextGatewayDiscount: orderQuantity >= constants.MIN_QUANTITY && orderQuantity < constants.GATEWAY_HALF_THRESHOLD ? constants.GATEWAY_HALF_THRESHOLD - orderQuantity : 0,
          nextGatewayIncluded: orderQuantity >= constants.GATEWAY_HALF_THRESHOLD && orderQuantity < constants.FLEX_MIN_QUANTITY ? constants.FLEX_MIN_QUANTITY - orderQuantity : 0,
          flexUnlockRemaining: orderQuantity < constants.FLEX_MIN_QUANTITY ? Math.max(0, constants.FLEX_MIN_QUANTITY - orderQuantity) : 0
        },
        constants: constants
      };
    }

    return {
      constants: constants,
      calculate: calculate,
      normalizeCollarQuantity: function (value) { return normalizeCollarQuantity(value, constants); },
      isValidCollarQuantity: function (value) { return isValidCollarQuantity(value, constants); },
      getOwnershipGatewayPrice: function (quantity) { return getOwnershipGatewayPrice(quantity, constants); },
      getOwnershipGatewaySavings: function (quantity) { return getOwnershipGatewaySavings(quantity, constants); },
      getFlexGatewayPrice: function (quantity) { return getFlexGatewayPrice(quantity, constants); },
      isFlexEligible: function (quantity) { return isFlexEligible(quantity, constants); }
    };
  }

  window.DairyMindPricingEngine = {
    constants: DEFAULT_CONSTANTS,
    create: create,
    normalizeCollarQuantity: normalizeCollarQuantity,
    isValidCollarQuantity: isValidCollarQuantity,
    getOwnershipGatewayPrice: function (quantity) { return getOwnershipGatewayPrice(quantity, DEFAULT_CONSTANTS); },
    getOwnershipGatewaySavings: function (quantity) { return getOwnershipGatewaySavings(quantity, DEFAULT_CONSTANTS); },
    getFlexGatewayPrice: function (quantity) { return getFlexGatewayPrice(quantity, DEFAULT_CONSTANTS); },
    isFlexEligible: function (quantity) { return isFlexEligible(quantity, DEFAULT_CONSTANTS); }
  };
})();
